// Lib
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import type { Editor } from '@tiptap/react';

// Utils
import { getElementId } from '../../../../../common/elements/utils/elementPropertyUtils';
import { getTaskListFeatureSuggestionState } from '../../../../../common/tiptap/extensions/taskList/taskListFeatureSuggestionUtils';
import { alreadySuggested } from '../../../suggestion/suggestionUtils';
import {
    getDeleteTasksSuggestionChange,
    getTitleSuggestionChange,
    SUGGEST,
    UNSUGGEST,
} from './taskListSuggestionTriggerUtils';
import type { SuggestionChange } from './taskListSuggestionTriggerUtils';

// Hooks
import useUpdatedRef from '../../../../utils/react/useUpdatedRef';

// Selectors
import { getElementLocalData } from '../../../local/elementLocalDataSelector';
import { getDeleteTasksSuggestionDismissed, getTitleSuggestionAck } from '../../taskListSelector';

// Actions
import { elementSuggestFeature, elementUnsuggestFeature } from '../../../suggestion/suggestionActions';

// Constants
import { FEATURE_SUGGESTIONS } from '../../../suggestion/suggestionConstants';

// Types
import type { ImMNElement } from '../../../../../common/elements/elementModelTypes';
import type { ImmutableMap } from '../../../../../common/utils/immutableHelper';

// Long enough that the suggestion never appears between keystrokes while a task is being typed
const TITLE_SUGGESTION_DEBOUNCE_MS = 1000;

type TaskListFeatureSuggestionTriggerArgs = {
    editor: Editor | null;
    element: ImMNElement;
};

/**
 * Suggests the task list features off the live editor document.
 *
 * The element's persisted content lags behind by the content save debounce - and a checkbox
 * toggle isn't flushed at all - so the triggers subscribe to editor updates instead.
 */
const useTaskListFeatureSuggestionTriggers = ({ editor, element }: TaskListFeatureSuggestionTriggerArgs): void => {
    const dispatch = useDispatch();
    const elementId = getElementId(element);
    const elementLocalData = useSelector((state: ImmutableMap) => getElementLocalData(state, { element }));

    // Read through a ref so the update subscription doesn't have to be torn down and
    // re-attached every time a suggestion or a dismissal flag changes.
    const suggestionStateRef = useUpdatedRef({
        isTitleAcknowledged: !!getTitleSuggestionAck(element),
        isDeleteTasksDismissed: !!getDeleteTasksSuggestionDismissed(element),
        isTitleSuggested: alreadySuggested(elementLocalData, FEATURE_SUGGESTIONS.TITLE),
        isDeleteTasksSuggested: alreadySuggested(elementLocalData, FEATURE_SUGGESTIONS.DELETE_COMPLETED_TASKS),
    });

    const previousTopLevelTaskCountRef = useRef(0);

    useEffect(() => {
        if (!editor) return;

        /**
         * Dispatches the feature suggestion change, if there is one.
         */
        const applyChange = (feature: string, change: SuggestionChange) => {
            if (!change) return;

            const suggestionDetails = { id: elementId, feature };

            change === SUGGEST
                ? dispatch(elementSuggestFeature(suggestionDetails))
                : dispatch(elementUnsuggestFeature(suggestionDetails));
        };

        // Baseline the count from the loaded document so an existing untitled list with
        // many tasks isn't suggested a title as soon as it renders.
        previousTopLevelTaskCountRef.current = getTaskListFeatureSuggestionState(editor.state.doc).topLevelTaskCount;

        // Set once a task has been added, and stays set until the suggestion is made or cleared, so
        // that continuing to edit pushes the suggestion out rather than losing it.
        let isTitleSuggestionPending = false;

        /**
         * Ensures a pause in editing has passed before suggesting a title.
         */
        const suggestTitleAfterPause = debounce(() => {
            isTitleSuggestionPending = false;

            const { hasTitle, topLevelTaskCount } = getTaskListFeatureSuggestionState(editor.state.doc);

            // A title may have been added, or the new task removed again, during the pause
            if (hasTitle || topLevelTaskCount <= 1) return;

            applyChange(FEATURE_SUGGESTIONS.TITLE, SUGGEST);
        }, TITLE_SUGGESTION_DEBOUNCE_MS);

        /**
         * Applies a title suggestion change: a suggestion waits for the user to pause editing, while
         * clearing one (and any pending one) happens immediately.
         */
        const applyTitleChange = (change: SuggestionChange) => {
            if (change === UNSUGGEST) {
                isTitleSuggestionPending = false;
                suggestTitleAfterPause.cancel();
                applyChange(FEATURE_SUGGESTIONS.TITLE, UNSUGGEST);
                return;
            }

            if (change === SUGGEST) isTitleSuggestionPending = true;

            // Every edit restarts the pause, so the suggestion never appears mid-keystroke - the task
            // that triggered it is only announced once the user stops typing.
            if (isTitleSuggestionPending) suggestTitleAfterPause();
        };

        /**
         * Handle an editor update.
         */
        const onUpdate = () => {
            const documentState = getTaskListFeatureSuggestionState(editor.state.doc);
            const previousTopLevelTaskCount = previousTopLevelTaskCountRef.current;

            previousTopLevelTaskCountRef.current = documentState.topLevelTaskCount;

            const { isTitleAcknowledged, isDeleteTasksDismissed, isTitleSuggested, isDeleteTasksSuggested } =
                suggestionStateRef.current;

            const titleSuggestionChange = getTitleSuggestionChange({
                documentState,
                previousTopLevelTaskCount,
                isAcknowledged: isTitleAcknowledged,
                isSuggested: isTitleSuggested,
            });

            applyTitleChange(titleSuggestionChange);

            const deleteSuggestionChange = getDeleteTasksSuggestionChange({
                documentState,
                isDismissed: isDeleteTasksDismissed,
                isSuggested: isDeleteTasksSuggested,
            });

            applyChange(FEATURE_SUGGESTIONS.DELETE_COMPLETED_TASKS, deleteSuggestionChange);
        };

        // Whenever the editor changes, check to see if we should now make feature suggestions
        editor.on('update', onUpdate);

        // Turn off any listeners or awaiting callbacks when we unmount
        return () => {
            suggestTitleAfterPause.cancel();
            editor.off('update', onUpdate);
        };
    }, [dispatch, editor, elementId]);
};

export default useTaskListFeatureSuggestionTriggers;
